import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { useAssignmentContext } from '../context';
import StepSelectGroups from '../_components/StepSelectGroups';
import StepNotificationSettings from '../_components/StepNotificationSettings';
import StepDistributionSchedule from '../_components/StepDistributionSchedule';
import { Checkbox, Typography, Box } from '@mui/material';
import { getDTBPackage } from 'views/App/Portal/DTB/Packages/_data_source/useDTBPackage';
import { AppContext } from 'views/App/context';
import useManageAssignDTBPackage from '../_data_sources/useManageAssignDTBPackage';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import { isDtbPackageEligibleForAssignmentOrDistribution } from 'views/App/Portal/DTB/utils/dtbPackageEligibility';
import Loading from 'views/App/Portal/Loading';
import TruncatedText from '_lex/_atoms/TruncatedText';

const steps = [
  { title: t('dtbs.assignments.stepper.steps.assignments') },
  { title: t('dtbs.assignments.stepper.steps.notifications') },
  { title: t('dtbs.assignments.stepper.steps.schedule') },
];

const AssignStepper: React.FC = () => {
  const { id: dtbPackageId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrganizationId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [dtbPackage, setDTBPackage] = useState<DTBPackage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const manualId = dtbPackage?.manual?.id;
  const { selectedGroups, setSelectedGroups, notificationSettings, setNotificationSettings, schedule, setSchedule } =
    useAssignmentContext();
  const { assignToGroups } = useManageAssignDTBPackage();

  // Stepper state
  const [step, setStep] = useState(0);
  // Loading and feedback
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const fetchPackage = async () => {
      if (!currentOrganizationId || !dtbPackageId) {
        setIsLoading(false);
        return;
      }

      try {
        const packageData = await getDTBPackage(currentOrganizationId, dtbPackageId);
        if (!packageData.dtbs || packageData.dtbs.length === 0) {
          enqueueSnackbar("Can't assign an empty package", { variant: 'error' });
          navigate('..');
          return;
        }
        if (!isDtbPackageEligibleForAssignmentOrDistribution(packageData)) {
          enqueueSnackbar(t('dtbs.assignments.stepper.cannotAssignDraftPackage'), { variant: 'error' });
          navigate('..');
          return;
        }
        setDTBPackage(packageData);
      } catch (error) {
        enqueueSnackbar('Failed to load package data.', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackage();
  }, [currentOrganizationId, dtbPackageId]);

  // Navigation handlers
  const handleCancel = () => {
    navigate('..');
  };
  const handleNext = () => setStep((s) => Math.min(s + 1, 2));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  // Assignment API call
  const handleAssign = async () => {
    if (!currentOrganizationId || !dtbPackageId) return;
    if (!dtbPackage || !dtbPackage.dtbs || dtbPackage.dtbs.length === 0) {
      enqueueSnackbar('Cannot assign package: No DTBs found in package.', { variant: 'error' });
      return;
    }
    if (!isDtbPackageEligibleForAssignmentOrDistribution(dtbPackage)) {
      enqueueSnackbar(t('dtbs.assignments.stepper.cannotAssignDraftPackage'), { variant: 'error' });
      return;
    }
    setAssigning(true);
    // Map consolidateNotifications to API enum values
    let consolidateValue = notificationSettings.consolidate;
    if (consolidateValue === 'daily') consolidateValue = 'DAILY';
    else if (consolidateValue === 'weekly') consolidateValue = 'WEEKLY';
    else if (consolidateValue === 'none') consolidateValue = 'NONE';
    // Convert schedule.dueDate to ISO string with time for API (java.time.Instant expects this)
    const dueDateISO = schedule.dueDate ? new Date(schedule.dueDate).toISOString() : new Date().toISOString();
    // Build schedule array: one entry per DTB in the package
    const scheduleArray = (dtbPackage?.dtbs || []).map((dtb) => ({
      dueDate: {
        dueDate: dueDateISO,
        numberOfDays: 0,
        type: 'SPECIFIC_DATE',
      },
      id: dtb.id,
      startDay: {
        dayOfMonth: 0,
        type: 'ALL_AT_ONCE',
      },
    }));
    // console.log('scheduleArray', scheduleArray);
    const payload = {
      groups: selectedGroups.map((g) => ({ id: g.id })),
      notifications: {
        consolidateNotifications: consolidateValue,
        emailNotification: notificationSettings.email,
        enableManualReminder: notificationSettings.manual,
        overdueNotification: notificationSettings.overdue,
        pushNotification: notificationSettings.push,
        reminderNotification: false,
      },
      schedule: scheduleArray,
    };
    assignToGroups(
      currentOrganizationId,
      dtbPackageId,
      payload,
      () => {
        setAssigning(false);
        enqueueSnackbar('Package assigned successfully!', { variant: 'success' });
        queryClient.invalidateQueries(['dtbPackages']);
        queryClient.invalidateQueries(['dtbPackage', currentOrganizationId, dtbPackageId]);
        setTimeout(() => navigate('..'), 1200);
      },
      () => {
        setAssigning(false);
        enqueueSnackbar('Failed to assign package.', { variant: 'error' });
      }
    );
  };

  // Right drawer for selected groups (only show on step 0)
  const rightDrawer =
    step === 0
      ? {
          header: t('dtbs.assignments.stepper.selectedGroups'),
          content: (
            <Box sx={{ width: '100%' }}>
              {selectedGroups.length === 0 ? (
                <Typography
                  sx={{ px: 3, py: 2 }}
                  color='text.secondary'
                >
                  {t('dtbs.assignments.stepper.noGroupsSelected')}
                </Typography>
              ) : (
                <Box>
                  {selectedGroups.map((group, idx) => (
                    <Box
                      key={group.id}
                      sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: idx !== selectedGroups.length - 1 ? '1px solid #e0e3e7' : 'none',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <Checkbox
                        checked
                        size='large'
                        sx={{ color: 'success.main', mr: 1.5, '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        onChange={() => {
                          setSelectedGroups((prev) => prev.filter((g) => g.id !== group.id));
                        }}
                      />
                      <TruncatedText
                        text={group.name}
                        maxWidth='100%'
                        typographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                          sx: { fontWeight: 500, fontSize: 16, flex: 1 },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ),
          noPadding: true,
          wide: true,
        }
      : undefined;

  if (isLoading || !dtbPackage) {
    return <Loading />;
  }

  return (
    <TakeoverLayout
      pageTitle={dtbPackage?.manual?.name || 'Manual assignments'}
      backUrl='/admin/dtbs/assignments'
      rightDrawer={rightDrawer}
      contentHeaderLeft={
        <Typography
          variant='h5'
          sx={{ fontWeight: 600 }}
        >
          {steps[step].title}
        </Typography>
      }
    >
      {step === 0 && (
        <StepSelectGroups
          manualId={manualId || ''}
          onCancel={handleCancel}
          onNext={handleNext}
        />
      )}
      {step === 1 && (
        <StepNotificationSettings
          onBack={handleBack}
          onNext={handleNext}
          value={notificationSettings}
          onChange={setNotificationSettings}
        />
      )}
      {step === 2 && (
        <StepDistributionSchedule
          onBack={handleBack}
          onAssign={handleAssign}
          value={schedule}
          onChange={setSchedule}
          loading={assigning}
        />
      )}
    </TakeoverLayout>
  );
};

export default AssignStepper;
